import { useState, type MouseEvent } from "react";
import type { AccountHistoryPoint } from "../types";
import { money } from "../lib/format";
import { annualGrowthRate } from "../lib/chartMath";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type ValueKey = "account_value" | "margin_balance";
type Point = { time: number; value: number };
type Regression = { slope: number; intercept: number; origin: number };

const W = 720;
const H = 280;
const PAD_X = 8;
const PAD_Y = 12;
const LATEST_X = W / 2;
const DAY = 86_400_000;
const FORECAST_DAYS = 90;

function series(points: AccountHistoryPoint[], key: ValueKey): Point[] {
  return points
    .filter((point) => point[key] != null)
    .map((point) => ({ time: new Date(point.observed_at).getTime(), value: Number(point[key]) }))
    .filter((point) => Number.isFinite(point.time) && Number.isFinite(point.value));
}

function regression(points: Point[]): Regression | null {
  if (points.length < 2) return null;
  const origin = points[0].time;
  const samples = points.map((point) => ({ x: (point.time - origin) / DAY, y: point.value }));
  const xMean = samples.reduce((sum, point) => sum + point.x, 0) / samples.length;
  const yMean = samples.reduce((sum, point) => sum + point.y, 0) / samples.length;
  const denominator = samples.reduce((sum, point) => sum + (point.x - xMean) ** 2, 0);
  if (denominator === 0) return null;
  const slope = samples.reduce(
    (sum, point) => sum + (point.x - xMean) * (point.y - yMean),
    0,
  ) / denominator;
  return { slope, intercept: yMean - slope * xMean, origin };
}

function rate(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "CAGR —";
  return `CAGR ${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
}

export function BrokerageHistoryChart({ points }: { points: AccountHistoryPoint[] }) {
  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [hoveredTime, setHoveredTime] = useState<number | null>(null);
  const account = series(points, "account_value");
  const margin = series(points, "margin_balance");
  const allObserved = [...account, ...margin];
  if (allObserved.length === 0) return null;

  const firstTime = Math.min(...allObserved.map((point) => point.time));
  const lastTime = Math.max(...allObserved.map((point) => point.time));
  const forecastTime = lastTime + FORECAST_DAYS * DAY;
  const accountTrend = regression(account);
  const marginTrend = regression(margin);
  const predicted = (trend: Regression | null, time: number) =>
    trend == null ? null : Math.max(0, trend.intercept + trend.slope * ((time - trend.origin) / DAY));
  const accountForecast = predicted(accountTrend, forecastTime);
  const marginForecast = predicted(marginTrend, forecastTime);

  const trendValues = [
    predicted(accountTrend, firstTime), predicted(accountTrend, lastTime), accountForecast,
    predicted(marginTrend, firstTime), predicted(marginTrend, lastTime), marginForecast,
  ].filter((value): value is number => value != null);
  const maxValue = Math.max(...allObserved.map((point) => point.value), ...trendValues, 1);
  const minValue = Math.min(...allObserved.map((point) => point.value), ...trendValues, 0);
  const span = maxValue - minValue || 1;
  const x = (time: number) => {
    if (time <= lastTime) {
      if (lastTime === firstTime) return LATEST_X;
      return PAD_X + ((time - firstTime) / (lastTime - firstTime)) * (LATEST_X - PAD_X);
    }
    return LATEST_X + ((time - lastTime) / (forecastTime - lastTime)) * (W - PAD_X - LATEST_X);
  };
  const y = (value: number) => H - PAD_Y - ((value - minValue) / span) * (H - PAD_Y * 2);
  const path = (values: Point[]) => values.map((point, index) =>
    `${index === 0 ? "M" : "L"}${x(point.time).toFixed(1)},${y(point.value).toFixed(1)}`,
  ).join(" ");
  const trendPath = (trend: Regression | null, start: number, end: number) => {
    const a = predicted(trend, start);
    const b = predicted(trend, end);
    return a == null || b == null ? "" : `M${x(start)},${y(a)} L${x(end)},${y(b)}`;
  };
  const dateLabel = (time: number) => new Intl.DateTimeFormat(undefined, {
    month: "short", day: "numeric", year: "2-digit",
  }).format(time);
  const nearestPoint = (values: Point[], time: number) => values.reduce<Point | null>(
    (nearest, point) => nearest == null || Math.abs(point.time - time) < Math.abs(nearest.time - time)
      ? point
      : nearest,
    null,
  );
  const activeTime = hoveredTime ?? selectedTime;
  const selectionTime = activeTime == null
    ? null
    : Math.max(firstTime, Math.min(forecastTime, activeTime));
  const selectionIsProjection = selectionTime != null && selectionTime > lastTime;
  const selectedAccount = selectionTime == null
    ? null
    : selectionIsProjection
      ? predicted(accountTrend, selectionTime)
      : nearestPoint(account, selectionTime)?.value ?? null;
  const selectedMargin = selectionTime == null
    ? null
    : selectionIsProjection
      ? predicted(marginTrend, selectionTime)
      : nearestPoint(margin, selectionTime)?.value ?? null;

  const timeFromEvent = (event: MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return null;
    const chartX = Math.max(PAD_X, Math.min(W - PAD_X, ((event.clientX - rect.left) / rect.width) * W));
    const time = chartX <= LATEST_X
      ? lastTime === firstTime
        ? firstTime
        : firstTime + ((chartX - PAD_X) / (LATEST_X - PAD_X)) * (lastTime - firstTime)
      : lastTime + ((chartX - LATEST_X) / (W - PAD_X - LATEST_X)) * (forecastTime - lastTime);
    if (time <= lastTime) {
      return nearestPoint(allObserved, time)?.time ?? firstTime;
    }
    return Math.round(time / DAY) * DAY;
  };
  const onChartClick = (event: MouseEvent<SVGSVGElement>) => {
    const time = timeFromEvent(event);
    if (time != null) setSelectedTime(time);
  };
  const onChartHover = (event: MouseEvent<SVGSVGElement>) => {
    const time = timeFromEvent(event);
    if (time != null) setHoveredTime(time);
  };

  return (
    <Card className="gap-3 py-3" data-testid="brokerage-history">
      <CardHeader className="py-0">
        <CardTitle className="text-lg">Brokerage and margin history</CardTitle>
        <CardDescription>
          History, trends, and a 90-day outlook.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span className="size-2 rounded-full bg-primary" /> Brokerage value
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {rate(annualGrowthRate(points, "account_value"))}
              {accountForecast != null && ` · 90-day projection ${money(accountForecast)}`}
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span className="size-2 rounded-full bg-destructive" /> Margin balance
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {rate(annualGrowthRate(points, "margin_balance"))}
              {marginForecast != null && ` · 90-day projection ${money(marginForecast)}`}
            </div>
          </div>
        </div>

        <svg
          width="100%"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Brokerage value and margin balance history with trend lines and projections. Hover or click to inspect a date."
          className="cursor-crosshair"
          onClick={onChartClick}
          onMouseMove={onChartHover}
          onMouseLeave={() => setHoveredTime(null)}
        >
          <line x1={PAD_X} y1={H - PAD_Y} x2={W - PAD_X} y2={H - PAD_Y} stroke="var(--border)" />
          <line
            data-testid="brokerage-history-latest-boundary"
            x1={x(lastTime)}
            y1={0}
            x2={x(lastTime)}
            y2={H}
            stroke="var(--border)"
            strokeDasharray="4 4"
          />
          {selectionTime != null && (
            <line
              data-testid="brokerage-history-crosshair"
              x1={x(selectionTime)}
              y1={0}
              x2={x(selectionTime)}
              y2={H}
              stroke="var(--muted-foreground)"
              strokeWidth="1"
              strokeDasharray="3 3"
              opacity="0.6"
            />
          )}
          <path d={path(account)} fill="none" stroke="var(--primary)" strokeWidth="3" />
          <path d={path(margin)} fill="none" stroke="var(--destructive)" strokeWidth="3" />
          <path d={trendPath(accountTrend, firstTime, lastTime)} fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeDasharray="7 5" opacity="0.65" />
          <path d={trendPath(marginTrend, firstTime, lastTime)} fill="none" stroke="var(--destructive)" strokeWidth="1.5" strokeDasharray="7 5" opacity="0.65" />
          <path d={trendPath(accountTrend, lastTime, forecastTime)} fill="none" stroke="var(--primary)" strokeWidth="2" strokeDasharray="2 5" />
          <path d={trendPath(marginTrend, lastTime, forecastTime)} fill="none" stroke="var(--destructive)" strokeWidth="2" strokeDasharray="2 5" />
          {account.at(-1) && <circle cx={x(account.at(-1)!.time)} cy={y(account.at(-1)!.value)} r="4" fill="var(--primary)" />}
          {margin.at(-1) && <circle cx={x(margin.at(-1)!.time)} cy={y(margin.at(-1)!.value)} r="4" fill="var(--destructive)" />}
          {selectionTime != null && selectedAccount != null && (
            <circle cx={x(selectionTime)} cy={y(selectedAccount)} r="4" fill="var(--primary)" />
          )}
          {selectionTime != null && selectedMargin != null && (
            <circle cx={x(selectionTime)} cy={y(selectedMargin)} r="4" fill="var(--destructive)" />
          )}
        </svg>
        <div className="grid grid-cols-3 text-[10px] text-muted-foreground">
          <span>{dateLabel(firstTime)}</span>
          <span className="text-center">Latest · {dateLabel(lastTime)}</span>
          <span className="text-right">Projection · {dateLabel(forecastTime)}</span>
        </div>
        {selectionTime != null && (
          <div className="flex min-h-10 flex-wrap items-start justify-between gap-2 text-xs" data-testid="brokerage-history-selection">
            <div>
              <div className="text-[10px] text-muted-foreground">
                {selectionIsProjection ? "Projection" : "Observed"} · {dateLabel(selectionTime)}
              </div>
              <div className="flex flex-wrap gap-4">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block size-2 rounded-full bg-primary" />
                  Brokerage {selectedAccount == null ? "—" : money(selectedAccount)}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block size-2 rounded-full bg-destructive" />
                  Margin {selectedMargin == null ? "—" : money(selectedMargin)}
                </span>
              </div>
            </div>
            {selectedTime != null && (
              <button
                type="button"
                className="text-[10px] text-muted-foreground hover:text-foreground"
                onClick={() => setSelectedTime(null)}
              >
                Clear selection
              </button>
            )}
          </div>
        )}
        <p className="text-[10px] text-muted-foreground">
          Solid lines show observations, dashed lines show trends, and dotted lines show projections. Hover to preview; click to pin.
        </p>
      </CardContent>
    </Card>
  );
}
